import { Injectable, computed, inject } from "@angular/core";

import { CountriesApiService } from "@components/shared/countries/api";
import { RiskAssessmentReportRecord } from "@components/shared/risk-assessment-reports/models";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import {
  ILocationExtended,
  ILocationLinkExtended,
  IMaterial,
  IMaterialExtended,
  IProductExtended,
} from "@shared/interfaces";
import {
  AuthenticationService,
  CommonService,
  LocationsService,
  MaterialsService,
  ProductsService,
} from "@shared/services";
import { CommonUtils } from "@shared/utils";

type ReportRecord = RiskAssessmentReportRecord["record"];
type Material = IMaterial | IMaterialExtended;
type Params = { id: string; materials?: Material[] };

const { COUNTRY, LOCATION, MATERIAL, ORGANISATION, PRODUCT } = RiskAssessmentTemplateResourceType;

@Injectable({
  providedIn: "root",
})
export class ProductRiskAssessmentReportRelatedRecordsService {
  private readonly commonService = inject(CommonService);

  private readonly authService = inject(AuthenticationService);

  private readonly productsService = inject(ProductsService);

  private readonly materialsService = inject(MaterialsService);

  private readonly locationsService = inject(LocationsService);

  private readonly countriesApi = inject(CountriesApiService);

  private readonly countryOptionsMap = computed(
    () =>
      new Map(
        this.commonService
          .countriesOptions()
          .map((country) => [country.value.toString(), country.label]),
      ),
  );

  async get(params: Params): Promise<ReportRecord[]> {
    if (!this.authService.isRegularUser) {
      return null;
    }

    const relatedProducts = await this.getRelatedProducts(params.id);
    const { locationLinks, locations } = await this.getLocations(relatedProducts);
    const countries = await this.getCountries(locationLinks);
    const organisations = this.getOrganisations(locations);
    const materials = await this.getMaterials(params, relatedProducts);

    return [
      ...organisations,
      ...materials,
      ...countries,
      ...locations.map(({ id, name }) => ({ id, name, type: LOCATION })),
      ...relatedProducts.map(({ id, name }) => ({ id, name, type: PRODUCT })),
    ];
  }

  private async getRelatedProducts(productId: string) {
    const orgId = this.authService.getActiveOrganisationId();

    const relatedProducts = (
      await this.productsService.getProductSupplyChain(orgId, productId, ["FULL_LOCATION_LINKS"])
    ).loadProductSupplyChain?.products
      .map(({ product }) => product)
      .filter(({ id }) => id !== productId);

    return relatedProducts;
  }

  private async getLocations(relatedProducts: IProductExtended[]) {
    const locationLinksMap = new Map<string, ILocationLinkExtended>();

    relatedProducts.forEach(({ locationLinks }) =>
      locationLinks.forEach((location) => locationLinksMap.set(location.id, location)),
    );

    const locationLinks = Array.from(locationLinksMap.values());

    const locationIds = new Set<string>();

    locationLinksMap.forEach(({ from, to }) => {
      locationIds.add(from.id);
      locationIds.add(to.id);
    });

    const locations = await this.locationsService.getByIdsGraphQL(
      Array.from(locationIds),
      undefined,
    );

    return {
      locationLinks,
      locations,
    };
  }

  private async getCountries(locationLinks: ILocationLinkExtended[]): Promise<ReportRecord[]> {
    const countryCodes = Array.from(
      new Set(locationLinks.flatMap(({ from, to }) => [from.address.country, to.address.country])),
    );

    // TODO: use a new API to get all Countries by codes
    const { content } = await this.countriesApi.getAll({});
    const countryMap = new Map(content.map((country) => [country.code, country]));

    const countries = countryCodes.map((code) => {
      const country = countryMap.get(code) ?? {
        id: null,
        name: this.countryOptionsMap().get(code),
        code,
      };

      return { ...country, type: COUNTRY };
    });

    return countries;
  }

  private getOrganisations(locations: ILocationExtended[]): ReportRecord[] {
    const organisations = Array.from(
      new Map(
        locations.map(({ organisationId, organisationName }) => [
          organisationId,
          {
            id: organisationId,
            name: organisationName,
            type: ORGANISATION,
          },
        ]),
      ).values(),
    );

    return organisations;
  }

  private async getMaterials(product: Params, relatedProducts: IProductExtended[]) {
    if (!product.materials) {
      const { allowedMaterials } = await this.productsService.get(product.id);
      const ids = allowedMaterials.map((uri) => CommonUtils.getUriId(uri));

      product.materials = await this.materialsService.getByIdsGraphQL(ids);
    }

    const materials = Array.from(
      new Map([
        ...product.materials.map((material) => this.mapMaterial(material)),
        ...relatedProducts.flatMap((product) =>
          product.allowedMaterials.map((material) => this.mapMaterial(material)),
        ),
      ]).values(),
    );

    return materials;
  }

  private mapMaterial(material: IMaterial | IMaterialExtended): [string, ReportRecord] {
    return [material.id, { id: material.id, name: material.name, type: MATERIAL }];
  }
}
